import type { Dayjs } from "dayjs";
import type { SxProps, Theme } from "@mui/material/styles";
import { scheduledPhaseStartPickerMin } from "../../utils/scheduledBudgetEditPolicy";
import {
  formatPickerDayjsToEventWallMinute,
  inclusiveEndCalendarDayFromPhaseEndWall,
  resolveBudgetPhaseDateTimePickerDefaults,
  snapPickerDayjsToScheduledPhaseDayStartWallMinute,
  snapPickerDayjsToScheduledPhaseInclusiveEndDayWallMinute,
  snapPickerDayjsToWallMinute,
  snapWallMinuteToMetaBudgetUtc,
  wallMinuteToDayjsInEventTimezone,
} from "../../utils/eventWallTime";

/** Neighbor phase bounds so this row cannot steal the next phase's minimum Meta interval. */
export type PhaseCalendarNeighborBounds = {
  startMin?: Dayjs;
  endMax?: Dayjs;
};

export type PhaseDateTimeRangeInput = {
  phaseIndex: number;
  ariaLabels?: { start: string; end: string };
  eventTimezone: string;
  time_start: string;
  time_end: string;
  deliveryWindowInEventTimezone?: { start: Dayjs; end: Dayjs } | null;
  neighborPickerBounds?: PhaseCalendarNeighborBounds | null;
  alwaysShowRangeRow?: boolean;
  lockStartToDeliveryWindowStart?: boolean;
  lockEndToDeliveryWindowEnd?: boolean;
  disableStartPicker?: boolean;
  disableEndPicker?: boolean;
  capPhaseStartAtNow?: boolean;
  snapPickerToMetaUtcQuarterHour?: boolean;
  schedulePhasePickCalendarDaysOnly?: boolean;
  pickerMinutesStep?: number;
  minStartEndGapMinutes?: number;
  onChangeStart: (wall: string) => void;
  onChangeEnd: (wall: string) => void;
  invalidTimeField?: "start" | "end" | null;
  pickerOpenIconAtStart?: boolean;
};

type PickerSide = "start" | "end";

type DeliveryWindowBounds = { start: Dayjs | null; end: Dayjs | null };

/** How a single side of the range row is rendered. */
export type PhaseRangeFieldModel =
  | { mode: "hidden" }
  | { mode: "campaignBoundary"; label: string }
  | { mode: "readonly"; text: string }
  | {
      mode: "day";
      value: Dayjs | null;
      minDate?: Dayjs;
      maxDate?: Dayjs;
      placeholder: string;
      onChange: (day: Dayjs | null) => void;
    }
  | {
      mode: "dateTime";
      value: Dayjs | null;
      minDateTime?: Dayjs;
      maxDateTime?: Dayjs;
      placeholder: string;
      onChange: (instant: Dayjs | null) => void;
    };

export type PhaseDateTimeRangeViewModel = {
  timezone: string;
  startAriaLabel: string;
  endAriaLabel: string;
  layout: {
    showArrow: boolean;
    showEndSlot: boolean;
    startPlaceholder: string;
    pickerSlotFlex: { base: string; sm: number } | number;
  };
  start: PhaseRangeFieldModel;
  end: PhaseRangeFieldModel;
  dateTimePicker: {
    timeSteps: { hours: number; minutes: number; seconds?: number };
    closeOnSelect: boolean;
  };
  fieldSx: { start: SxProps<Theme>; end: SxProps<Theme> };
  pickerOpenIconAtStart: boolean;
  invalidTimeField: "start" | "end" | null;
};

function validDayjs(value: Dayjs | undefined): Dayjs | null {
  return value?.isValid() ? value : null;
}

function deliveryWindowBounds(
  deliveryWindow: { start: Dayjs; end: Dayjs } | null | undefined
): DeliveryWindowBounds {
  if (!deliveryWindow) return { start: null, end: null };
  return {
    start: validDayjs(deliveryWindow.start),
    end: validDayjs(deliveryWindow.end),
  };
}

function wallToPickerValue(params: {
  wallMinute: string;
  timezone: string;
  snapMeta: boolean;
  side: PickerSide;
}): Dayjs | null {
  const wall = params.wallMinute?.trim();
  if (!wall) return null;
  const { timezone: tz, snapMeta, side } = params;
  if (!snapMeta) return wallMinuteToDayjsInEventTimezone(wall, tz);
  const snapped = snapWallMinuteToMetaBudgetUtc(wall, tz, side);
  return wallMinuteToDayjsInEventTimezone(snapped || wall, tz);
}

function sideState(params: {
  wallMinute: string;
  lockToDeliveryWindow: boolean;
  deliveryBoundary: Dayjs | null;
  timezone: string;
  snapMeta: boolean;
  side: PickerSide;
}): {
  lockedToDeliveryWindow: boolean;
  pickerValue: Dayjs | null;
  countsAsSet: boolean;
} {
  const lockedToDeliveryWindow = Boolean(
    params.lockToDeliveryWindow && params.deliveryBoundary
  );
  const pickerValue = lockedToDeliveryWindow
    ? params.deliveryBoundary
    : wallToPickerValue({
        wallMinute: params.wallMinute,
        timezone: params.timezone,
        snapMeta: params.snapMeta,
        side: params.side,
      });
  return {
    lockedToDeliveryWindow,
    pickerValue,
    countsAsSet: Boolean(params.wallMinute?.trim()) || lockedToDeliveryWindow,
  };
}

function earliest(...candidates: Array<Dayjs | null | undefined>): Dayjs | undefined {
  const ok = candidates.filter((d): d is Dayjs => !!d?.isValid());
  if (!ok.length) return undefined;
  return ok.reduce((a, b) => (a.isBefore(b) ? a : b));
}

function latest(...candidates: Array<Dayjs | null | undefined>): Dayjs | undefined {
  const ok = candidates.filter((d): d is Dayjs => !!d?.isValid());
  if (!ok.length) return undefined;
  return ok.reduce((a, b) => (a.isAfter(b) ? a : b));
}

function dateTimeMinMax(
  min?: Dayjs,
  max?: Dayjs
): { minDateTime?: Dayjs; maxDateTime?: Dayjs } {
  const minOk = min?.isValid() ? min : undefined;
  const maxOk = max?.isValid() ? max : undefined;
  if (!minOk && !maxOk) return {};
  if (!minOk) return { maxDateTime: maxOk };
  if (!maxOk) return { minDateTime: minOk };
  if (minOk.isAfter(maxOk) || minOk.isSame(maxOk)) return {};
  return { minDateTime: minOk, maxDateTime: maxOk };
}

function calendarDayMinMax(
  min?: Dayjs,
  max?: Dayjs
): { minDate?: Dayjs; maxDate?: Dayjs } {
  const minOk = min?.isValid() ? min.startOf("day") : undefined;
  const maxOk = max?.isValid() ? max.startOf("day") : undefined;
  if (!minOk && !maxOk) return {};
  if (!minOk) return { maxDate: maxOk };
  if (!maxOk) return { minDate: minOk };
  if (minOk.isAfter(maxOk) || minOk.isSame(maxOk)) return {};
  return { minDate: minOk, maxDate: maxOk };
}

function pickerMinMax(params: {
  deliveryWindow: DeliveryWindowBounds;
  startValue: Dayjs | null;
  endValue: Dayjs | null;
  minGapMinutes: number;
  neighbors: PhaseCalendarNeighborBounds | null;
  capStartAtNow: boolean;
  timezone: string;
}) {
  const { deliveryWindow, startValue, endValue, minGapMinutes } = params;
  const minEnd = startValue?.isValid()
    ? startValue.add(minGapMinutes, "minute")
    : undefined;
  const maxStart = endValue?.isValid()
    ? endValue.subtract(minGapMinutes, "minute")
    : undefined;
  const deliveryMaxStart = deliveryWindow.end?.isValid()
    ? deliveryWindow.end.subtract(minGapMinutes, "minute")
    : undefined;
  const minFromNow = params.capStartAtNow
    ? scheduledPhaseStartPickerMin(params.timezone)
    : null;

  const startMin = latest(
    deliveryWindow.start,
    params.neighbors?.startMin,
    minFromNow?.isValid() ? minFromNow : undefined
  );
  const startMax = earliest(maxStart, deliveryMaxStart);
  const endMin = latest(minEnd, deliveryWindow.start ?? undefined);
  const endMax = earliest(deliveryWindow.end, params.neighbors?.endMax);

  return {
    start: dateTimeMinMax(startMin, startMax),
    end: dateTimeMinMax(endMin, endMax),
  };
}

function buildFieldSx(params: {
  alwaysShowRangeRow: boolean;
  hasStart: boolean;
  openIconAtStart: boolean;
}): { start: SxProps<Theme>; end: SxProps<Theme> } {
  const rootBase = {
    fontSize: "14px",
    fontFamily: "inherit",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    minHeight: "40px",
    alignItems: "center",
    backgroundColor: "transparent",
    "& fieldset": {
      borderWidth: 0,
      borderColor: "transparent",
      borderRadius: 0,
      boxShadow: "none",
    },
    "&:hover fieldset": { borderColor: "transparent" },
    "&.Mui-focused fieldset": {
      borderWidth: 0,
      borderColor: "transparent",
      boxShadow: "none",
    },
    "& .MuiInputBase-input": {
      fontWeight: 500,
      color: "#4A5568",
      letterSpacing: "-0.42px",
      py: 0,
      px: 0,
      minWidth: 0,
      minHeight: "24px",
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      lineHeight: "24px",
    },
    "& .MuiPickersSectionList-root, & .MuiPickersInputBase-sectionsContainer": {
      display: "flex",
      alignItems: "center",
      minHeight: "24px",
      lineHeight: "24px",
    },
    "& .MuiInputAdornment-root": {
      marginInlineStart: "2px",
      marginInlineEnd: 0,
    },
    "& .MuiIconButton-root": { padding: "4px" },
    "& .MuiButtonBase-root": {
      color: "rgba(0,0,0,0.45)",
      cursor: "pointer",
    },
    cursor: "pointer",
  };

  const shell: SxProps<Theme> = {
    "& .MuiOutlinedInput-root": rootBase,
  };

  const startShell: SxProps<Theme> = {
    ...shell,
    "& .MuiOutlinedInput-root": {
      ...rootBase,
      ...(!params.alwaysShowRangeRow && !params.hasStart
        ? { minWidth: "min(100%, 9.5rem)" }
        : {}),
    },
  };

  const adornmentAtStart = (sx: SxProps<Theme>): SxProps<Theme> => {
    if (!params.openIconAtStart) return sx;
    const base = sx as Record<string, unknown>;
    const root = base["& .MuiOutlinedInput-root"] as Record<string, unknown>;
    return {
      ...base,
      "& .MuiOutlinedInput-root": {
        ...root,
        "& .MuiInputAdornment-root": {
          marginInlineStart: 0,
          marginInlineEnd: "2px",
        },
      },
    };
  };

  return {
    start: adornmentAtStart(startShell),
    end: adornmentAtStart(shell),
  };
}

const PHASE_DATETIME_FORMAT = "MMM D h:mm A";
const PHASE_DATE_FORMAT = "MMM D, YYYY";

function readonlyDisplayText(params: {
  side: PickerSide;
  dayPick: boolean;
  timezone: string;
  pickerValue: Dayjs | null;
  wallMinute: string;
  inclusiveEndDay: Dayjs | null;
}): string {
  const { pickerValue, wallMinute, dayPick, timezone: tz, side } = params;
  if (pickerValue?.isValid()) {
    if (dayPick) {
      if (side === "end" && params.inclusiveEndDay?.isValid()) {
        return params.inclusiveEndDay.format(PHASE_DATE_FORMAT);
      }
      return pickerValue.tz(tz).startOf("day").format(PHASE_DATE_FORMAT);
    }
    return pickerValue.format(PHASE_DATETIME_FORMAT);
  }
  return wallMinute || "";
}

function resolveFieldModel(params: {
  side: PickerSide;
  lockedToDeliveryWindow: boolean;
  disabled: boolean;
  dayPick: boolean;
  snapMeta: boolean;
  timezone: string;
  deliveryWindow: DeliveryWindowBounds;
  pickerValue: Dayjs | null;
  wallMinute: string;
  minMax: { minDateTime?: Dayjs; maxDateTime?: Dayjs };
  placeholder: string;
  onChangeWall: (wall: string) => void;
}): PhaseRangeFieldModel {
  const {
    side,
    lockedToDeliveryWindow,
    disabled,
    dayPick,
    snapMeta,
    timezone: tz,
    deliveryWindow,
    pickerValue,
    wallMinute,
    minMax,
    placeholder,
    onChangeWall,
  } = params;

  if (lockedToDeliveryWindow) {
    return {
      mode: "campaignBoundary",
      label: side === "start" ? "Campaign start" : "Campaign end",
    };
  }

  if (disabled) {
    const inclusiveEndDay =
      side === "end" && dayPick && wallMinute.trim()
        ? inclusiveEndCalendarDayFromPhaseEndWall(wallMinute, tz)
        : null;
    return {
      mode: "readonly",
      text: readonlyDisplayText({
        side,
        dayPick,
        timezone: tz,
        pickerValue,
        wallMinute,
        inclusiveEndDay,
      }),
    };
  }

  if (dayPick) {
    const dayBounds = calendarDayMinMax(
      minMax.minDateTime,
      minMax.maxDateTime
    );
    const value =
      side === "start"
        ? pickerValue?.isValid()
          ? pickerValue.tz(tz).startOf("day")
          : null
        : wallMinute.trim()
          ? inclusiveEndCalendarDayFromPhaseEndWall(wallMinute, tz)
          : null;
    return {
      mode: "day",
      value: value?.isValid() ? value : null,
      ...dayBounds,
      placeholder,
      onChange: (day) =>
        onChangeWall(
          day
            ? side === "start"
              ? snapPickerDayjsToScheduledPhaseDayStartWallMinute(
                  day,
                  tz,
                  deliveryWindow.start
                )
              : snapPickerDayjsToScheduledPhaseInclusiveEndDayWallMinute(
                  day,
                  tz,
                  deliveryWindow.end
                )
            : ""
        ),
    };
  }

  return {
    mode: "dateTime",
    value: pickerValue,
    minDateTime: minMax.minDateTime,
    maxDateTime: minMax.maxDateTime,
    placeholder,
    onChange: (instant) =>
      onChangeWall(
        snapMeta
          ? snapPickerDayjsToWallMinute(instant, tz, side)
          : formatPickerDayjsToEventWallMinute(instant, tz)
      ),
  };
}

/** Single pass from props → layout + discriminated start/end field models. */
export function buildPhaseDateTimeRangeViewModel(
  input: PhaseDateTimeRangeInput
): PhaseDateTimeRangeViewModel {
  const snapMeta = input.snapPickerToMetaUtcQuarterHour ?? true;
  const dayPick = Boolean(
    input.schedulePhasePickCalendarDaysOnly && snapMeta
  );
  const { pickerMinutesStep, minStartEndGapMinutes } =
    resolveBudgetPhaseDateTimePickerDefaults({
      snapPickerToMetaUtcQuarterHour: snapMeta,
      pickerMinutesStep: input.pickerMinutesStep,
      minStartEndGapMinutes: input.minStartEndGapMinutes,
    });

  const timezone = input.eventTimezone?.trim() || "UTC";
  const deliveryWindow = deliveryWindowBounds(
    input.deliveryWindowInEventTimezone
  );
  const neighbors = input.neighborPickerBounds ?? null;
  const alwaysShowRangeRow = input.alwaysShowRangeRow ?? false;

  const startSide = sideState({
    wallMinute: input.time_start,
    lockToDeliveryWindow: input.lockStartToDeliveryWindowStart ?? false,
    deliveryBoundary: deliveryWindow.start,
    timezone,
    snapMeta,
    side: "start",
  });
  const endSide = sideState({
    wallMinute: input.time_end,
    lockToDeliveryWindow: input.lockEndToDeliveryWindowEnd ?? false,
    deliveryBoundary: deliveryWindow.end,
    timezone,
    snapMeta,
    side: "end",
  });

  const minMax = pickerMinMax({
    deliveryWindow,
    startValue: startSide.pickerValue,
    endValue: endSide.pickerValue,
    minGapMinutes: minStartEndGapMinutes,
    neighbors,
    capStartAtNow: input.capPhaseStartAtNow ?? false,
    timezone,
  });

  const showEndSlot = alwaysShowRangeRow || startSide.countsAsSet;
  const showArrow =
    alwaysShowRangeRow || (startSide.countsAsSet && endSide.countsAsSet);
  const startPlaceholder = alwaysShowRangeRow
    ? "Start"
    : !startSide.countsAsSet
      ? "Select date range"
      : "Start";

  const start = resolveFieldModel({
    side: "start",
    lockedToDeliveryWindow: startSide.lockedToDeliveryWindow,
    disabled: input.disableStartPicker ?? false,
    dayPick,
    snapMeta,
    timezone,
    deliveryWindow,
    pickerValue: startSide.pickerValue,
    wallMinute: input.time_start,
    minMax: minMax.start,
    placeholder: startPlaceholder,
    onChangeWall: input.onChangeStart,
  });

  const end: PhaseRangeFieldModel = showEndSlot
    ? resolveFieldModel({
        side: "end",
        lockedToDeliveryWindow: endSide.lockedToDeliveryWindow,
        disabled: input.disableEndPicker ?? false,
        dayPick,
        snapMeta,
        timezone,
        deliveryWindow,
        pickerValue: endSide.pickerValue,
        wallMinute: input.time_end,
        minMax: minMax.end,
        placeholder: dayPick ? "End (inclusive)" : "End",
        onChangeWall: input.onChangeEnd,
      })
    : { mode: "hidden" };

  return {
    timezone,
    startAriaLabel:
      input.ariaLabels?.start ??
      `Phase ${input.phaseIndex + 1} start date and time`,
    endAriaLabel:
      input.ariaLabels?.end ?? `Phase ${input.phaseIndex + 1} end date and time`,
    layout: {
      showArrow,
      showEndSlot,
      startPlaceholder,
      pickerSlotFlex: showEndSlot ? { base: "1 1 100%", sm: 1 } : 1,
    },
    start,
    end,
    dateTimePicker: {
      timeSteps: snapMeta
        ? { hours: 1, minutes: pickerMinutesStep, seconds: 0 }
        : { hours: 1, minutes: pickerMinutesStep },
      closeOnSelect: snapMeta,
    },
    fieldSx: buildFieldSx({
      alwaysShowRangeRow,
      hasStart: startSide.countsAsSet,
      openIconAtStart: input.pickerOpenIconAtStart ?? false,
    }),
    pickerOpenIconAtStart: input.pickerOpenIconAtStart ?? false,
    invalidTimeField: input.invalidTimeField ?? null,
  };
}

/** Public API for {@link CampaignRunRangePicker} — no Meta snap, always show start + end. */
export type CampaignRunRangePickerProps = {
  eventTimezone: string;
  startAt: string;
  endAt: string;
  onChangeStartAt: (wall: string) => void;
  onChangeEndAt: (wall: string) => void;
  isStartAtDisabled?: boolean;
  isScheduleRangeReadOnly?: boolean;
  pickerOpenIconAtStart?: boolean;
  invalidTimeField?: "start" | "end" | null;
};

export function toCampaignRunRangeInput(
  props: CampaignRunRangePickerProps
): PhaseDateTimeRangeInput {
  const readOnly = props.isScheduleRangeReadOnly === true;
  return {
    phaseIndex: 0,
    eventTimezone: props.eventTimezone,
    time_start: props.startAt,
    time_end: props.endAt,
    deliveryWindowInEventTimezone: null,
    neighborPickerBounds: null,
    alwaysShowRangeRow: true,
    snapPickerToMetaUtcQuarterHour: false,
    schedulePhasePickCalendarDaysOnly: false,
    ariaLabels: {
      start: "Campaign run start date and time",
      end: "Campaign run end date and time",
    },
    onChangeStart: props.onChangeStartAt,
    onChangeEnd: props.onChangeEndAt,
    disableStartPicker: readOnly || props.isStartAtDisabled,
    disableEndPicker: readOnly,
    pickerOpenIconAtStart: props.pickerOpenIconAtStart,
    invalidTimeField: props.invalidTimeField,
  };
}

/** Public API for {@link ScheduledPhaseRangePicker} — Meta-snapped calendar-day phases. */
export type ScheduledPhaseRangePickerProps = {
  phaseIndex: number;
  eventTimezone: string;
  time_start: string;
  time_end: string;
  deliveryWindowInEventTimezone: { start: Dayjs; end: Dayjs } | null;
  neighborPickerBounds: PhaseCalendarNeighborBounds | null;
  isFirstPhase: boolean;
  isLastPhase: boolean;
  isCompletedPhase?: boolean;
  capPhaseStartAtNow?: boolean;
  invalidTimeField?: "start" | "end" | null;
  onChangeStart: (wall: string) => void;
  onChangeEnd: (wall: string) => void;
};

function scheduledPhaseAriaLabels(
  phaseIndex: number,
  isFirstPhase: boolean,
  isLastPhase: boolean
): { start: string; end: string } | undefined {
  if (!isFirstPhase && !isLastPhase) return undefined;
  const n = phaseIndex + 1;
  return {
    start: isFirstPhase
      ? "Campaign schedule start"
      : `Phase ${n} start date and time`,
    end: isLastPhase
      ? "Campaign schedule end"
      : `Phase ${n} end date and time`,
  };
}

export function toScheduledPhaseRangeInput(
  props: ScheduledPhaseRangePickerProps
): PhaseDateTimeRangeInput {
  const lockToDeliveryWindow = Boolean(props.deliveryWindowInEventTimezone);
  const completed = props.isCompletedPhase ?? false;
  return {
    phaseIndex: props.phaseIndex,
    eventTimezone: props.eventTimezone,
    time_start: props.time_start,
    time_end: props.time_end,
    deliveryWindowInEventTimezone: props.deliveryWindowInEventTimezone,
    neighborPickerBounds: props.neighborPickerBounds,
    ariaLabels: scheduledPhaseAriaLabels(
      props.phaseIndex,
      props.isFirstPhase,
      props.isLastPhase
    ),
    lockStartToDeliveryWindowStart:
      props.isFirstPhase && lockToDeliveryWindow,
    lockEndToDeliveryWindowEnd: props.isLastPhase && lockToDeliveryWindow,
    disableStartPicker: completed,
    disableEndPicker: completed,
    capPhaseStartAtNow: props.capPhaseStartAtNow,
    snapPickerToMetaUtcQuarterHour: true,
    schedulePhasePickCalendarDaysOnly: true,
    pickerOpenIconAtStart: true,
    onChangeStart: props.onChangeStart,
    onChangeEnd: props.onChangeEnd,
    invalidTimeField: props.invalidTimeField,
  };
}
